import { cn } from "~/lib/utils";
import React from "react";

// // // //

interface Props {
    src: string | null;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    draggable?: boolean;
    fallbackSrc?: string;
    fallbackClassName?: string;
    loading?: "eager" | "lazy";
}

/**
 * A component that displays an image and falls back to a gray box with alt text if the image fails to load.
 *
 * @param {string} props.src - The URL of the image to display.
 * @param {string} props.alt - The alt text for the image.
 * @param {string} [props.className] - An optional class name for the component.
 * @param {string} [props.draggable] - An optional draggable property for the component (defaults to "false")
 * @param {string} [props.fallbackSrc] - An optional fallback "src" property
 * @param {string} [props.fallbackClassName] - An optional className to apply for errors
 * @param {string} [props.loading] - An optional native img loading strategy ("eager" | "lazy")
 *
 * @returns {JSX.Element} - The rendered component.
 */
export function ImageLoader(props: Props) {
    const {
        src,
        alt,
        width,
        height,
        className,
        fallbackSrc,
        draggable = false,
        fallbackClassName,
        loading,
    } = props;

    // Define a state variable for tracking whether the image failed to load.
    const [error, setError] = React.useState(true);
    const imgRef = React.useRef<HTMLImageElement>(null);

    // Unset error if src changes
    React.useEffect(() => {
        setError(false);
    }, [src]);

    // Handle both onError and onLoad to catch ORB/CORS errors
    const handleImageError = () => {
        setError(true);
    };

    const handleImageLoad = () => {
        // Check if image actually loaded - naturalWidth/Height will be 0 if blocked by ORB/CORS
        if (imgRef.current && imgRef.current.naturalWidth === 0) {
            setError(true);
        }
    };

    // If the image failed to load, or if props.src is falsey,
    // render either fallbackSrc or a gray box with the alt text
    if (error || !src) {
        if (fallbackSrc) {
            return (
                <img
                    draggable={draggable}
                    className={cn(className, fallbackClassName)}
                    src={fallbackSrc}
                    alt={alt}
                    width={width}
                    height={height}
                    loading={loading}
                />
            );
        }

        return (
            <div
                draggable={draggable}
                className={cn(className, fallbackClassName)}
                // TODO - use TailwindCSS here for background
                // style={{ backgroundColor: "lightgray" }}
            >
                {alt}
            </div>
        );
    }

    // Otherwise, render the image with the given src and alt text, and attach the error handler.
    return (
        <img
            ref={imgRef}
            draggable={draggable}
            className={className}
            src={src}
            width={width}
            height={height}
            alt={alt}
            loading={loading}
            onError={handleImageError}
            onLoad={handleImageLoad}
        />
    );
}
